// Iter-207v — Monthly Cabinet Statement page.
//
// Aggregates, for a chosen (year, month) :
//   • Expenses — NDF paid + Cabinet expenses dated in the month, grouped by NDF category.
//   • Payments received — paid invoices + payment declarations, grouped by client.
// Lets the CEO/ADMIN/GESTIONNAIRE download the report as PDF / Excel / CSV
// and send it by email with a tracking pixel for read-receipt.
import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import ExtraAttachmentsPicker from "@/components/ExtraAttachmentsPicker";
import { toast } from "sonner";
import {
  FileText, FileSpreadsheet, FileType2, Send, Mail, Calendar, ChevronLeft, ChevronRight, ChevronDown, Loader2, Eye, EyeOff,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import BankReconPanel from "../components/BankReconPanel";
import DebtTimeline from "../components/DebtTimeline";

const MONTH_LABELS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const fmtMad = (n) =>
  (Number(n) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SourceBadge = ({ source }) => {
  const map = {
    ndf: { label: "NDF", tone: "bg-amber-50 text-amber-700 border-amber-200" },
    cabinet: { label: "Cabinet", tone: "bg-purple-50 text-purple-700 border-purple-200" },
    invoice: { label: "Facture", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    declaration: { label: "Déclaration", tone: "bg-sky-50 text-sky-700 border-sky-200" },
    bank_adjust: { label: "Ajustement", tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  };
  const cfg = map[source] || { label: source, tone: "bg-zinc-50 text-zinc-700 border-zinc-200" };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${cfg.tone}`}>
      {cfg.label}
    </span>
  );
};

// Iter-254 — audit trail of the AI bank reconciliation, shown on the operation line.
const ReconAuditBadge = ({ recon }) => {
  if (!recon) return null;
  const when = String(recon.matched_at || "").slice(0, 10);
  const hasDelta = Math.abs(recon.delta || 0) >= 0.01;
  const txt =
    `Rapproché par IA le ${when} avec la transaction « ${recon.bank_label || ""} » (${recon.bank_date || "—"})` +
    (hasDelta
      ? ` — écart de ${fmtMad(recon.delta)} MAD ${recon.applied ? "appliqué au Monthly Report" : "non appliqué"}`
      : " — aucun écart") +
    (recon.reason ? `\nMotif IA : ${recon.reason}` : "");
  return (
    <span
      title={txt}
      data-testid="recon-audit-badge"
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border bg-teal-50 text-teal-700 border-teal-200 cursor-help"
    >
      Rapproché IA{hasDelta ? ` · ${recon.delta > 0 ? "+" : ""}${fmtMad(recon.delta)}` : ""}
    </span>
  );
};

export default function MonthlyStatement() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedExp, setExpandedExp] = useState(() => new Set());
  const [expandedPay, setExpandedPay] = useState(() => new Set());
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [reconRefresh, setReconRefresh] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/monthly-statement?year=${year}&month=${month}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec du chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = async () => {
    try {
      const r = await api.get(`/admin/monthly-statement/email-history?year=${year}&month=${month}`);
      setHistory(r.data.items || []);
    } catch (e) {
      toast.error("Historique indisponible");
    }
  };

  const downloadExport = async (fmt) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/monthly-statement/export.${fmt}?year=${year}&month=${month}`;
    // Open in a new tab — browser handles the binary download via the Content-Disposition header.
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) {
      toast.error(`Export ${fmt} échoué (HTTP ${r.status})`);
      return;
    }
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `etat_mensuel_${year}-${String(month).padStart(2, "0")}.${fmt}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const years = useMemo(() => {
    const cy = today.getFullYear();
    const arr = [];
    for (let y = cy - 5; y <= cy + 1; y += 1) arr.push(y);
    return arr;
  }, [today]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5" data-testid="monthly-statement-page">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">État mensuel</h1>
          <p className="text-xs text-zinc-500 mt-1">
            Dépenses (NDF payées + Cabinet) et règlements (factures + déclarations) du mois.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-zinc-400" />
          <button
            type="button"
            onClick={() => { if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1); }}
            data-testid="monthly-statement-month-prev"
            title="Mois précédent"
            className="h-9 w-9 inline-flex items-center justify-center border border-zinc-200 rounded-md bg-white hover:bg-zinc-50"
          >
            <ChevronLeft size={15} />
          </button>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            data-testid="monthly-statement-month-select"
            className="h-9 px-2 text-sm border border-zinc-200 rounded-md bg-white"
          >
            {MONTH_LABELS_FR.map((label, idx) => (
              <option key={idx + 1} value={idx + 1}>{label}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            data-testid="monthly-statement-year-select"
            className="h-9 px-2 text-sm border border-zinc-200 rounded-md bg-white"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            type="button"
            onClick={() => { if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1); }}
            data-testid="monthly-statement-month-next"
            title="Mois suivant"
            className="h-9 w-9 inline-flex items-center justify-center border border-zinc-200 rounded-md bg-white hover:bg-zinc-50"
          >
            <ChevronRight size={15} />
          </button>
          <button
            type="button"
            onClick={() => { setReconRefresh((n) => n + 1); load(); }}
            data-testid="monthly-statement-refresh"
            className="h-9 px-3 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50"
          >
            Actualiser
          </button>
        </div>
      </div>

      {/* Iter-251 — Bank statement reconciliation */}
      <BankReconPanel year={year} month={month} onIntegrated={load} refreshKey={reconRefresh} onDetectedMonth={(y, m) => { setYear(y); setMonth(m); }} />

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="monthly-statement-summary">
          <SummaryCard label="Total dépenses" value={data.summary.total_expenses_mad} testid="summary-expenses" tone="rose" subtitle={`Cabinet ${fmtMad(data.summary.total_cabinet_expenses_mad || 0)} · NDF ${fmtMad(data.summary.total_ndf_mad || 0)} MAD`} />
          {data.summary.salaries_bank_filtered ? (
            <>
              <SummaryCard
                label="Salaires nets versés"
                value={(data.summary.total_net_salaries_paid_mad || 0) + (data.summary.total_salary_arrears_paid_mad || 0)}
                testid="summary-salaries" tone="rose"
                subtitle={(() => {
                  const arr = (data.salaries?.retro_details || []).filter((d) => d.kind === "salary");
                  const ym = (o) => `${String(o).slice(5, 7)}/${String(o).slice(0, 4)}`;
                  const base = `Salaire(s) de ${data.period_label} : ${fmtMad(data.summary.total_net_salaries_paid_mad || 0)} MAD`;
                  return arr.length === 0 ? base
                    : `${base} · Arriérés réglés : ${fmtMad(data.summary.total_salary_arrears_paid_mad)} MAD (${arr.map((d) => `${d.user_name} ${ym(d.origin)}`).join(", ")})`;
                })()}
              />
              <SummaryCard
                label="Charges payées ce mois"
                value={data.summary.total_charges_paid_mad || 0}
                testid="summary-charges-paid" tone="rose"
                subtitle={(data.salaries?.retro_details || []).filter((d) => d.kind === "charges").length > 0
                  ? `Rétroactif — mois couverts : ${[...new Set((data.salaries?.retro_details || []).filter((d) => d.kind === "charges").map((d) => d.origin))].join(", ")}`
                  : "Aucun paiement de charges détecté — bouton « Dette charges » sur une ligne du relevé pour requalifier"}
              />
            </>
          ) : (
            <SummaryCard
              label="Salaires (coût employeur)"
              value={data.summary.total_salaries_mad || 0}
              testid="summary-salaries" tone="rose"
              subtitle={`${data.summary.n_salary_lines || 0} salarié(s) — théorique (aucun relevé rapproché)`}
            />
          )}
          <SummaryCard label="Total règlements" value={data.summary.total_payments_mad} testid="summary-payments" tone="emerald" subtitle={`${data.summary.n_payment_lines} ligne(s)`} />
          <SummaryCard
            label="Net provisoire"
            value={data.summary.net_provisional_mad ?? data.summary.net_after_salaries_mad ?? data.summary.net_mad}
            testid="summary-net-after-salaries"
            tone={(data.summary.net_provisional_mad ?? data.summary.net_after_salaries_mad ?? data.summary.net_mad) >= 0 ? "emerald" : "rose"}
            highlight
            subtitle="Règlements − décaissements réels du mois"
          />
        </div>
      )}

      {/* Iter-273 — payroll debt row: unpaid salaries + deferred charges + real NET. */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="monthly-statement-debt-summary">
          <SummaryCard
            label="Dette salaires"
            value={data.summary.debt_salaries_mad || 0}
            testid="summary-debt-salaries" tone="amber"
            subtitle={`${data.summary.n_debt_salaries || 0} salaire(s) impayé(s) cumulés${(data.summary.n_salaries_unpaid || 0) > 0 ? ` · dont ${data.summary.n_salaries_unpaid} ce mois` : ""}`}
          />
          <SummaryCard
            label="Dette charges sociales"
            value={data.summary.debt_charges_mad || 0}
            testid="summary-debt-charges" tone="amber"
            subtitle={`${data.summary.n_debt_charges || 0} mois-salarié(s) en attente (CNSS / AMO / IR)`}
          />
          <SummaryCard
            label="Dette totale"
            value={data.summary.debt_total_mad || 0}
            testid="summary-debt-total" tone="amber"
            subtitle="Cumul à fin de mois (salaires + charges)"
          />
          <SummaryCard
            label="Net réel"
            value={data.summary.net_real_mad ?? data.summary.net_after_salaries_mad ?? 0}
            testid="summary-net-real"
            tone={(data.summary.net_real_mad ?? 0) >= 0 ? "emerald" : "rose"}
            highlight
            subtitle="Net provisoire − dette totale restante"
          />
        </div>
      )}

      {/* Iter-274 — month-by-month social debt evolution. */}
      {data && <DebtTimeline year={year} month={month} />}

      {/* Iter-207bb — VAT recap row (only when MAD lines carry a VAT breakdown). */}
      {data && (data.summary.total_expenses_vat_mad || 0) > 0 && (
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-3"
          data-testid="monthly-statement-vat-summary"
        >
          <div className="px-4 py-2.5 rounded-lg border border-blue-100 bg-blue-50/40">
            <div className="text-[10px] uppercase font-mono tracking-wider text-blue-700">Dépenses HT (MAD)</div>
            <div className="text-lg font-mono mt-0.5 text-blue-900" data-testid="summary-vat-ht">{fmtMad(data.summary.total_expenses_ht_mad)}</div>
          </div>
          <div className="px-4 py-2.5 rounded-lg border border-blue-100 bg-blue-50/40">
            <div className="text-[10px] uppercase font-mono tracking-wider text-blue-700">TVA collectée (MAD)</div>
            <div className="text-lg font-mono mt-0.5 text-blue-900" data-testid="summary-vat-amount">{fmtMad(data.summary.total_expenses_vat_mad)}</div>
          </div>
          <div className="px-4 py-2.5 rounded-lg border border-blue-100 bg-blue-50/40">
            <div className="text-[10px] uppercase font-mono tracking-wider text-blue-700">Dépenses TTC (MAD)</div>
            <div className="text-lg font-mono mt-0.5 text-blue-900" data-testid="summary-vat-ttc">{fmtMad(data.summary.total_expenses_ttc_mad)}</div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => downloadExport("pdf")} data-testid="monthly-statement-export-pdf">
          <FileText size={14} className="mr-2" /> PDF
        </Button>
        <Button variant="outline" onClick={() => downloadExport("xlsx")} data-testid="monthly-statement-export-xlsx">
          <FileSpreadsheet size={14} className="mr-2" /> Excel
        </Button>
        <Button variant="outline" onClick={() => downloadExport("csv")} data-testid="monthly-statement-export-csv">
          <FileType2 size={14} className="mr-2" /> CSV
        </Button>
        <Button onClick={() => setEmailModalOpen(true)} data-testid="monthly-statement-open-email">
          <Send size={14} className="mr-2" /> Envoyer par email
        </Button>
        <Button
          variant="outline"
          onClick={async () => { await loadHistory(); setHistoryOpen(true); }}
          data-testid="monthly-statement-open-history"
        >
          <Mail size={14} className="mr-2" /> Historique des envois
        </Button>
      </div>

      {loading && !data && <div className="text-sm text-zinc-500 py-8 text-center">Chargement…</div>}

      {/* Iter-249 — Cabinet expenses and NDF in separate sections. */}
      {data && (
        <ExpenseSection
          title="Dépenses cabinet"
          subtitle="Factures fournisseurs, abonnements et frais de structure + factures freelances payées"
          categories={data.cabinet_expenses_by_category || []}
          totalMad={data.summary.total_cabinet_expenses_mad || 0}
          sectionKey="cabinet"
          expanded={expandedExp}
          setExpanded={setExpandedExp}
          testid="monthly-statement-cabinet-expenses"
          onReload={load}
        />
      )}
      {data && (
        <ExpenseSection
          title="Notes de frais"
          subtitle="NDF collaborateurs payées dans le mois (remboursements)"
          categories={data.ndf_by_category || []}
          totalMad={data.summary.total_ndf_mad || 0}
          sectionKey="ndf"
          expanded={expandedExp}
          setExpanded={setExpandedExp}
          testid="monthly-statement-ndf-expenses"
        />
      )}

      {/* Iter-228y — Salaires section (SALARIE_MA cost employer). */}
      {data && (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="monthly-statement-salaries">
          <div className="px-4 py-2.5 bg-zinc-50/60 border-b border-zinc-100 flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-sm font-semibold text-zinc-900">Salaires — coût employeur</h2>
              <div className="text-[10px] uppercase font-mono tracking-wider text-zinc-500 mt-0.5">
                Coût employeur = net (versé au salarié) + charges sociales calculées automatiquement (CNSS/AMO salariales + IR + charges patronales) — taux paramétrables via Settings › Masse salariale
              </div>
              {data.salaries?.bank_filtered && (
                <div className="text-[10px] font-mono text-blue-700 mt-0.5" data-testid="salaries-bank-filtered-note">
                  🏦 Relevé bancaire rapproché : seuls les salaires de {data.period_label} trouvés sur le relevé sont comptés ici (× nombre d&apos;occurrences). Les salaires de mois précédents payés sur ce relevé sont listés plus bas en « arriérés ».
                </div>
              )}
            </div>
            <span className="text-xs font-mono text-zinc-500">{fmtMad(data.salaries?.total_cost_employer_mad || 0)} MAD</span>
          </div>
          {(data.salaries?.items || []).length === 0 && (
            <div className="px-4 py-6 text-sm text-zinc-400 text-center">
              Aucun salarié (SALARIE_MA) actif ce mois. Renseignez le brut mensuel via Admin › Utilisateurs › Rémunération.
            </div>
          )}
          {(data.salaries?.skipped || []).length > 0 && (
            <div className="mx-4 my-3 bg-amber-50 border border-amber-300 rounded-md px-3 py-2 space-y-1" data-testid="salaries-skipped-banner">
              <div className="text-xs font-semibold text-amber-900">
                ⚠ {data.salaries.skipped.length} salarié(s) exclu(s) du calcul ce mois :
              </div>
              {data.salaries.skipped.map((s, i) => (
                <div key={i} className="text-[11px] text-amber-800 font-mono" data-testid={`salaries-skipped-${s.user_id}`}>
                  {s.user_name} — {s.reason}
                </div>
              ))}
              <div className="text-[10px] text-amber-700">
                Corrigez via Admin › Utilisateurs › Rémunération (historique : supprimez les lignes erronées avec la poubelle).
              </div>
            </div>
          )}
          {(data.salaries?.items || []).length > 0 && (
            <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
            <table className="w-full min-w-[600px] md:min-w-0 text-xs">
              <thead>
                <tr className="text-zinc-500 font-mono uppercase tracking-wider text-[10px] border-b border-zinc-100">
                  <th className="text-left px-3 py-1.5">Collaborateur</th>
                  <th className="text-left px-3 py-1.5">Type emploi</th>
                  <th className="text-right px-3 py-1.5">Net mensuel</th>
                  <th className="text-right px-3 py-1.5">Charges sociales</th>
                  <th className="text-right px-3 py-1.5">Coût employeur</th>
                  <th className="text-left px-3 py-1.5">Effectif au</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {(data.salaries?.items || []).map((it) => (
                  <tr key={it.user_id} className="hover:bg-zinc-50/40" data-testid={`salary-row-${it.user_id}`}>
                    <td className="px-3 py-1.5">
                      {it.user_name}
                      {(it.bank_occurrences || 0) > 1 && (
                        <span data-testid={`salary-occurrences-${it.user_id}`} title={`Trouvé ${it.bank_occurrences} fois sur le relevé bancaire — montants multipliés`} className="ml-1.5 px-1.5 py-0.5 text-[9px] font-mono bg-blue-50 text-blue-700 border border-blue-200 rounded">
                          ×{it.bank_occurrences}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5"><span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">{it.employment_type}</span></td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtMad(it.salaire_net_mad)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-zinc-500">{fmtMad(it.employer_charges_mad)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtMad(it.cost_employer_mad)}</td>
                    <td className="px-3 py-1.5 font-mono text-zinc-500 text-[10px]">{it.effective_from || "—"}</td>
                  </tr>
                ))}
                <tr className="bg-zinc-50/60 border-t border-zinc-200 font-semibold" data-testid="salary-total-row">
                  <td className="px-3 py-1.5" colSpan={2}>TOTAL</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtMad((data.salaries?.items || []).reduce((s, i) => s + (Number(i.salaire_net_mad) || 0), 0))}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtMad(data.salaries?.total_employer_charges_mad || 0)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmtMad(data.salaries?.total_cost_employer_mad || 0)}</td>
                  <td className="px-3 py-1.5" />
                </tr>
              </tbody>
            </table>
            </div>
          )}
          {(data.salaries?.retro_details || []).some((d) => d.kind === "salary") && (
            <div className="mx-4 my-3 border border-blue-200 bg-blue-50/50 rounded-md px-3 py-2 space-y-1" data-testid="salaries-arrears-block">
              <div className="text-xs font-semibold text-blue-900">Salaires de mois précédents réglés sur ce relevé (arriérés)</div>
              {data.salaries.retro_details.filter((d) => d.kind === "salary").map((d, i) => (
                <div key={i} className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-blue-900" data-testid={`salaries-arrear-${i}`}>
                  <span className="font-medium">{d.user_name}</span>
                  <span className="px-1 rounded text-[10px] font-mono uppercase bg-white border border-blue-200">Salaire {String(d.origin).slice(5, 7)}/{String(d.origin).slice(0, 4)}</span>
                  <span className="text-zinc-500 truncate flex-1 min-w-[140px]">{d.date || ""} {d.label || ""}</span>
                  <span className="font-mono font-semibold">{fmtMad(d.amount_mad)} MAD</span>
                </div>
              ))}
              <div className="text-[10px] text-blue-800/80">
                Comptés dans les décaissements du mois (« Salaires nets versés »), mais pas dans le coût employeur de {data.period_label} ci-dessus : ils appartiennent à leur mois d&apos;origine et soldent la « Dette salaires ».
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payments by client */}
      {data && (() => {
        // Iter-241 — foreign-currency payments with NO declared MAD value and
        // NO configured FX rate are counted at face value: surface a warning.
        const missingFx = [];
        (data.payments_by_client || []).forEach((b) => (b.items || []).forEach((it) => {
          if (it.fx === "missing_rate") missingFx.push(it);
        }));
        if (missingFx.length === 0) return null;
        return (
          <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 space-y-1.5" data-testid="missing-fx-banner">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
              <span aria-hidden="true">⚠</span>
              {missingFx.length} règlement(s) en devise sans contre-valeur MAD ni taux de change configuré
            </div>
            <p className="text-xs text-amber-800 leading-relaxed">
              Ces montants sont comptés au nominal de la devise (faux en MAD). Renseignez la contre-valeur via le bouton
              MAD sur la page <a href="/invoices?payment_status=paid" className="underline font-medium">Factures</a>, ou
              configurez le taux dans Settings → Masse salariale (Taux de change).
            </p>
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {missingFx.map((it, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono bg-white border border-amber-200 text-amber-900 rounded" data-testid={`missing-fx-item-${it.reference || i}`}>
                  {it.reference || "—"} · {(it.amount_original ?? 0).toLocaleString("fr-FR")} {it.currency}
                </span>
              ))}
            </div>
          </div>
        );
      })()}
      {data && (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="monthly-statement-payments">
          <div className="px-4 py-2.5 bg-zinc-50/60 border-b border-zinc-100 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-zinc-900">Règlements par client</h2>
            <span className="text-xs font-mono text-zinc-500">{fmtMad(data.summary.total_payments_mad)} MAD</span>
          </div>
          {data.payments_by_client.length === 0 && (
            <div className="px-4 py-6 text-sm text-zinc-400 text-center">Aucun règlement sur la période.</div>
          )}
          {data.payments_by_client.map((cli) => (
            <CategoryGroup
              key={cli.client_name}
              title={cli.client_name}
              total={cli.total_mad}
              count={cli.items.length}
              open={expandedPay.has(cli.client_name)}
              onToggle={() => {
                setExpandedPay((p) => {
                  const next = new Set(p);
                  if (next.has(cli.client_name)) next.delete(cli.client_name); else next.add(cli.client_name);
                  return next;
                });
              }}
              testidBase={`payment-client-${cli.client_name}`}
            >
              <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
              <table className="w-full min-w-[600px] md:min-w-0 text-xs">
                <thead>
                  <tr className="text-zinc-500 font-mono uppercase tracking-wider text-[10px]">
                    <th className="text-left px-3 py-1.5">Date</th>
                    <th className="text-left px-3 py-1.5">Source</th>
                    <th className="text-left px-3 py-1.5">Référence</th>
                    <th className="text-left px-3 py-1.5">Mission</th>
                    <th className="text-right px-3 py-1.5">Montant MAD</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {cli.items.map((it, idx) => (
                    <tr key={(it.invoice_id || it.declaration_id || idx)} className="hover:bg-zinc-50/40">
                      <td className="px-3 py-1.5 font-mono text-zinc-700">{(it.date || "").slice(0, 10)}</td>
                      <td className="px-3 py-1.5">
                        <span className="inline-flex items-center gap-1">
                          <SourceBadge source={it.source} />
                          <ReconAuditBadge recon={it.bank_recon} />
                        </span>
                      </td>
                      <td className="px-3 py-1.5 font-mono text-zinc-500">{it.reference || ""}</td>
                      <td className="px-3 py-1.5 truncate max-w-xs">{it.mission_title || "—"}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-semibold">
                        {fmtMad(it.amount_mad)}
                        {it.currency && it.currency !== "MAD" && (
                          <span className="ml-1 text-[10px] text-zinc-400 font-normal">
                            ({(it.amount_original ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} {it.currency}{it.fx === "missing_rate" ? " — taux manquant" : ""})
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </CategoryGroup>
          ))}
        </div>
      )}

      <EmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        year={year}
        month={month}
        periodLabel={data?.period_label || `${String(month).padStart(2, "0")}/${year}`}
        onSent={() => { setEmailModalOpen(false); loadHistory(); setHistoryOpen(true); }}
      />

      <HistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        items={history}
        periodLabel={data?.period_label || `${String(month).padStart(2, "0")}/${year}`}
      />
    </div>
  );
}


const SummaryCard = ({ label, value, subtitle, testid, tone = "zinc", highlight = false }) => {
  const toneMap = {
    rose: "border-rose-200 bg-rose-50/60 text-rose-900",
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
    amber: "border-amber-200 bg-amber-50/60 text-amber-900",
    zinc: "border-zinc-200 bg-zinc-50/60 text-zinc-900",
  };
  return (
    <div
      data-testid={testid}
      className={`px-4 py-3 rounded-lg border ${toneMap[tone]} ${highlight ? "ring-2 ring-offset-2 ring-emerald-300/40" : ""}`}
    >
      <div className="text-[11px] uppercase font-mono tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold font-mono mt-1">{fmtMad(value)} MAD</div>
      {subtitle && <div className="text-[11px] opacity-60 mt-0.5">{subtitle}</div>}
    </div>
  );
};


// Iter-251 — inline justificatif upload for « Sans justif » bank-statement lines.
const JustifUpload = ({ line, onDone }) => {
  const ref = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/bank-recon/cabinet-lines/${line.cabinet_line_id}/justificatif`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      toast.success("Justificatif enregistré" + (r.data?.drive_link ? " et archivé sur Drive" : ""));
      if (r.data?.drive_warning) toast.warning(r.data.drive_warning);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload du justificatif échoué");
    } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300" data-testid={`no-receipt-badge-${line.cabinet_line_id}`}>
        Sans justif
      </span>
      <input ref={ref} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      <button
        type="button" disabled={busy}
        onClick={(e) => { e.stopPropagation(); ref.current?.click(); }}
        className="text-[10px] underline text-sky-700 hover:text-sky-900 disabled:opacity-50"
        data-testid={`upload-justif-${line.cabinet_line_id}`}
      >
        {busy ? "Envoi…" : "+ justificatif"}
      </button>
    </span>
  );
};


const ExpenseSection = ({ title, subtitle, categories, totalMad, sectionKey, expanded, setExpanded, testid, onReload }) => (
  <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid={testid}>
    <div className="px-4 py-2.5 bg-zinc-50/60 border-b border-zinc-100 flex items-baseline justify-between flex-wrap gap-2">
      <div>
        <h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
        {subtitle && <div className="text-[10px] uppercase font-mono tracking-wider text-zinc-500 mt-0.5">{subtitle}</div>}
      </div>
      <span className="text-xs font-mono text-zinc-500">{fmtMad(totalMad)} MAD</span>
    </div>
    {categories.length === 0 && (
      <div className="px-4 py-6 text-sm text-zinc-400 text-center">Aucune dépense sur la période.</div>
    )}
    {categories.map((cat) => {
      const setKey = `${sectionKey}:${cat.category}`;
      return (
        <CategoryGroup
          key={setKey}
          title={cat.category}
          total={cat.total_mad}
          count={cat.items.length}
          open={expanded.has(setKey)}
          onToggle={() => {
            setExpanded((p) => {
              const next = new Set(p);
              if (next.has(setKey)) next.delete(setKey); else next.add(setKey);
              return next;
            });
          }}
          testidBase={`${sectionKey}-expense-cat-${cat.category}`}
        >
          <div className="overflow-x-auto overscroll-x-contain" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full min-w-[600px] md:min-w-0 text-xs">
            <thead>
              <tr className="text-zinc-500 font-mono uppercase tracking-wider text-[10px]">
                <th className="text-left px-3 py-1.5">Date</th>
                <th className="text-left px-3 py-1.5">Source</th>
                <th className="text-left px-3 py-1.5">Fournisseur</th>
                <th className="text-left px-3 py-1.5">Référence</th>
                <th className="text-left px-3 py-1.5">Collab.</th>
                <th className="text-right px-3 py-1.5">HT (MAD)</th>
                <th className="text-right px-3 py-1.5">TVA%</th>
                <th className="text-right px-3 py-1.5">TVA (MAD)</th>
                <th className="text-right px-3 py-1.5">TTC (MAD)</th>
                <th className="text-right px-3 py-1.5">Montant MAD</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {cat.items.map((it, idx) => (
                <tr
                  key={(it.expense_line_id || it.cabinet_line_id || it.freelance_invoice_id || idx)}
                  className={it.no_receipt ? "bg-amber-50/80 hover:bg-amber-100/60" : "hover:bg-zinc-50/40"}
                >
                  <td className="px-3 py-1.5 font-mono text-zinc-700">{it.date || ""}</td>
                  <td className="px-3 py-1.5">
                    <span className="inline-flex items-center gap-1">
                      <SourceBadge source={it.source} />
                      <ReconAuditBadge recon={it.bank_recon} />
                      {it.origin === "BANK_STATEMENT" && (
                        <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200" data-testid={`bank-origin-badge-${it.cabinet_line_id}`}>
                          Relevé bancaire
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 max-w-xs">
                    <span className="truncate">{it.vendor}</span>
                    {it.no_receipt && (
                      <span className="ml-1.5"><JustifUpload line={it} onDone={onReload} /></span>
                    )}
                    {!it.no_receipt && it.origin === "BANK_STATEMENT" && it.receipt_drive_web_view_link && (
                      <a href={it.receipt_drive_web_view_link} target="_blank" rel="noreferrer" className="ml-1.5 text-[10px] underline text-sky-700" data-testid={`justif-link-${it.cabinet_line_id}`}>justif</a>
                    )}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-zinc-500">{it.reference || ""}</td>
                  <td className="px-3 py-1.5">{it.user_name || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-zinc-600">
                    {it.amount_ht != null ? fmtMad(it.amount_ht) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-zinc-600">
                    {it.vat_rate != null ? `${Number(it.vat_rate).toFixed(0)}%` : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-zinc-600">
                    {it.vat_amount != null ? fmtMad(it.vat_amount) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-zinc-600">
                    {it.amount_ttc != null ? fmtMad(it.amount_ttc) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtMad(it.amount_mad)}</td>
                </tr>
              ))}
              {(cat.total_vat_mad > 0) && (
                <tr className="bg-zinc-50/60 font-mono text-[10px] text-zinc-700">
                  <td className="px-3 py-1.5" colSpan={5}>Sous-total TVA (MAD)</td>
                  <td className="px-3 py-1.5 text-right font-semibold">{fmtMad(cat.total_ht_mad || 0)}</td>
                  <td className="px-3 py-1.5"></td>
                  <td className="px-3 py-1.5 text-right font-semibold">{fmtMad(cat.total_vat_mad || 0)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold">{fmtMad(cat.total_ttc_mad || 0)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold">{fmtMad(cat.total_mad)}</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </CategoryGroup>
      );
    })}
  </div>
);


const CategoryGroup = ({ title, total, count, open, onToggle, testidBase, children }) => {
  return (
    <div className="border-b border-zinc-100 last:border-b-0" data-testid={testidBase}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-2 flex items-center gap-3 hover:bg-zinc-50/60 text-left"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="font-medium text-sm text-zinc-900">{title}</span>
        <span className="text-xs text-zinc-500 font-mono">{count} ligne(s)</span>
        <span className="ml-auto text-sm font-mono font-semibold text-zinc-900">{fmtMad(total)} MAD</span>
      </button>
      {open && <div className="px-2 pb-2 bg-zinc-50/40">{children}</div>}
    </div>
  );
};


function EmailModal({ open, onClose, year, month, periodLabel, onSent }) {
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ to: "", cc: "", subject: "", body: "", formats: ["pdf", "xlsx", "csv"] });
  // Iter-315 — pick a supplier: its contact email is added to À / Cc.
  const [suppliers, setSuppliers] = useState([]);
  const [supplierTarget, setSupplierTarget] = useState("to");
  useEffect(() => {
    if (!open) return;
    api.get("/suppliers").then((r) => setSuppliers(Array.isArray(r.data) ? r.data : [])).catch(() => setSuppliers([]));
  }, [open]);
  const addSupplierEmail = (sid) => {
    const email = suppliers.find((x) => x.supplier_id === sid)?.contact_email;
    if (!email) return;
    setForm((f) => {
      const list = (f[supplierTarget] || "").split(",").map((x) => x.trim()).filter(Boolean);
      if (list.some((x) => x.toLowerCase() === email.toLowerCase())) return f;
      return { ...f, [supplierTarget]: [...list, email].join(", ") };
    });
  };
  const [sending, setSending] = useState(false);
  // Iter-239 — supporting-documents ZIP package.
  const [includeZip, setIncludeZip] = useState(true);
  const [pkg, setPkg] = useState(null);
  const [pkgProgress, setPkgProgress] = useState(null);
  // Iter-280 — attach the month's bank statement PDF (checked by default).
  const [includeStatement, setIncludeStatement] = useState(true);
  const [recon, setRecon] = useState(null);
  // Iter-281 — ZIP of freelance invoices paid during the month.
  const [includeFreelanceZip, setIncludeFreelanceZip] = useState(true);
  const [flPaid, setFlPaid] = useState(null);

  const loadPkgStatus = async () => {
    try {
      const r = await api.get(`/admin/monthly-statement/package-status?year=${year}&month=${month}`);
      setPkg(r.data);
      return r.data;
    } catch { return null; }
  };

  useEffect(() => {
    if (!open) return;
    setIncludeZip(true);
    setPkgProgress(null);
    setIncludeStatement(true);
    setRecon(null);
    api.get(`/admin/bank-recon?year=${year}&month=${month}`)
      .then((r) => setRecon(r.data?.recon_id ? r.data : null))
      .catch(() => setRecon(null));
    loadPkgStatus();
    (async () => {
      try {
        const r = await api.get(`/admin/monthly-statement/email-preview?year=${year}&month=${month}`);
        setPreview(r.data);
        setForm({
          to: r.data.to || "",
          cc: r.data.cc || "",
          subject: r.data.subject || "",
          body: r.data.body || "",
          formats: ["pdf", "xlsx", "csv"],
        });
      } catch (e) {
        toast.error("Pré-remplissage du mail échoué");
      }
    })();
  }, [open, year, month]);

  const toggleFmt = (f) => {
    setForm((p) => ({
      ...p,
      formats: p.formats.includes(f) ? p.formats.filter((x) => x !== f) : [...p.formats, f],
    }));
  };

  const [extraAtts, setExtraAtts] = useState([]);
  const ensurePackage = async (force = false) => {
    let st = force ? null : (pkg?.status === "DONE" ? pkg : null);
    if (st) return st;
    await api.post("/admin/monthly-statement/package", { year, month, force });
    setPkgProgress({ progress: 0, step: "Initialisation…" });
    for (let i = 0; i < 400; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      st = await loadPkgStatus();
      if (!st) continue;
      setPkgProgress({ progress: st.progress || 0, step: st.step || "" });
      if (st.status === "DONE") { setPkgProgress(null); return st; }
      if (st.status === "ERROR") { setPkgProgress(null); throw new Error(st.error || "Échec du packaging"); }
    }
    setPkgProgress(null);
    throw new Error("Timeout du packaging");
  };

  const regenerateZip = async () => {
    setSending(true);
    try {
      await ensurePackage(true);
      toast.success("ZIP des pièces justificatives régénéré");
    } catch (e) {
      toast.error(e?.message || "Échec du packaging");
    } finally { setSending(false); }
  };

  const submit = async () => {
    if (!form.to.trim()) { toast.error("Au moins un destinataire (To) est requis."); return; }
    if (form.formats.length === 0) { toast.error("Sélectionnez au moins un format de pièce jointe."); return; }
    // Iter-280/281 — warn once for every attachment that will be missing
    // (bank statement, freelance invoices ZIP): unchecked or unavailable.
    const statementAvailable = !!recon?.recon_id;
    const willAttachStatement = includeStatement && statementAvailable;
    const freelanceAvailable = (flPaid?.count || 0) > 0;
    const willAttachFreelance = includeFreelanceZip && freelanceAvailable;
    const warnings = [];
    if (!willAttachStatement) {
      warnings.push(!statementAvailable
        ? `• Relevé bancaire : aucun relevé uploadé pour ${periodLabel}.`
        : "• Relevé bancaire : pièce jointe décochée.");
    }
    if (!willAttachFreelance) {
      warnings.push(!freelanceAvailable
        ? `• Factures freelances : aucune facture payée sur ${periodLabel}.`
        : "• Factures freelances : pièce jointe décochée.");
    }
    if (warnings.length > 0) {
      if (!window.confirm(
        `L'envoi sera INCOMPLET :\n\n${warnings.join("\n")}\n\nOK = envoyer quand même · Annuler = annuler l'envoi`,
      )) return;
    }
    setSending(true);
    try {
      if (includeZip) {
        await ensurePackage(false);
      }
      await api.post("/admin/monthly-statement/send-email", {
        year, month,
        to: form.to, cc: form.cc, subject: form.subject, body: form.body,
        formats: form.formats,
        include_package: includeZip,
        include_bank_statement: includeStatement && !!recon?.recon_id,
        include_freelance_zip: includeFreelanceZip && (flPaid?.count || 0) > 0,
        extra_attachment_ids: extraAtts.map((a) => a.attachment_id),
      });
      toast.success(`Email envoyé pour ${periodLabel}`);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Envoi échoué");
    } finally {
      setSending(false);
      setPkgProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="monthly-statement-email-modal" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Envoyer l&apos;état mensuel — {periodLabel}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {preview && (
            <div className="text-xs text-zinc-500">
              De : <span className="font-mono text-zinc-700">{preview.from_alias}</span>
            </div>
          )}
          {suppliers.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="email-modal-supplier-picker">
              <label className="text-xs font-medium text-zinc-700">Fournisseur :</label>
              <select value="" onChange={(e) => addSupplierEmail(e.target.value)}
                className="h-8 px-2 border border-zinc-200 rounded-md text-xs bg-white flex-1 min-w-[180px]"
                data-testid="email-modal-supplier-select">
                <option value="">— Choisir un fournisseur (ajoute son email de contact) —</option>
                {suppliers.map((sp) => (
                  <option key={sp.supplier_id} value={sp.supplier_id} disabled={!sp.contact_email}>
                    {`${sp.name}${sp.contact_name ? ` — ${sp.contact_name}` : ""} · ${sp.contact_email || "pas d'email de contact"}`}
                  </option>
                ))}
              </select>
              <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden text-[11px]" data-testid="email-modal-supplier-target">
                {["to", "cc"].map((t) => (
                  <button key={t} type="button" onClick={() => setSupplierTarget(t)}
                    className={`px-2 h-8 transition-colors ${supplierTarget === t ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                    data-testid={`email-modal-supplier-target-${t}`}>
                    {t === "to" ? "→ À" : "→ Cc"}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-zinc-700 block mb-1">À (séparés par virgule)</label>
            <input
              type="text" data-testid="email-modal-to"
              value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })}
              placeholder="comptable@cabinet.ma, ceo@baim.ma"
              className="w-full h-9 px-2 border border-zinc-200 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-700 block mb-1">Cc (optionnel)</label>
            <input
              type="text" data-testid="email-modal-cc"
              value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })}
              className="w-full h-9 px-2 border border-zinc-200 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-700 block mb-1">Sujet</label>
            <input
              type="text" data-testid="email-modal-subject"
              value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full h-9 px-2 border border-zinc-200 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-700 block mb-1">Corps</label>
            <textarea
              data-testid="email-modal-body"
              value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={9}
              className="w-full px-2 py-1.5 border border-zinc-200 rounded-md text-sm font-mono"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-700 block mb-1">Pièces jointes</label>
            <div className="flex gap-2">
              {["pdf", "xlsx", "csv"].map((f) => (
                <label key={f} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs cursor-pointer ${form.formats.includes(f) ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-zinc-200 text-zinc-500"}`}>
                  <input
                    type="checkbox" checked={form.formats.includes(f)}
                    onChange={() => toggleFmt(f)} data-testid={`email-modal-fmt-${f}`}
                    className="h-3 w-3"
                  />
                  {f.toUpperCase()}
                </label>
              ))}
            </div>
          </div>
          {/* Iter-280 — bank statement PDF attachment */}
          <div className="border border-zinc-200 rounded-md p-3 space-y-1.5" data-testid="email-modal-statement-block">
            <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer">
              <input
                type="checkbox" checked={includeStatement}
                onChange={(e) => setIncludeStatement(e.target.checked)}
                data-testid="email-modal-include-statement" className="h-3 w-3"
              />
              Joindre le relevé bancaire du mois
            </label>
            {recon?.recon_id ? (
              <p className="text-[11px] text-zinc-500 font-mono" data-testid="email-modal-statement-ready">
                {recon.filename || "relevé"} · {recon.bank || "banque"} — joint en PDF parmi les éléments du mois
              </p>
            ) : (
              <p className="text-[11px] text-amber-700" data-testid="email-modal-statement-missing">
                ⚠ Aucun relevé bancaire uploadé pour {periodLabel} — une confirmation sera demandée avant l&apos;envoi.
              </p>
            )}
          </div>
          {/* Iter-281 — freelance invoices ZIP */}
          <div className="border border-zinc-200 rounded-md p-3 space-y-1.5" data-testid="email-modal-freelance-block">
            <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer">
              <input
                type="checkbox" checked={includeFreelanceZip}
                onChange={(e) => setIncludeFreelanceZip(e.target.checked)}
                data-testid="email-modal-include-freelance" className="h-3 w-3"
              />
              Joindre le ZIP des factures freelances payées du mois
            </label>
            {(flPaid?.count || 0) > 0 ? (
              <p className="text-[11px] text-zinc-500 font-mono" data-testid="email-modal-freelance-ready">
                {flPaid.count} facture(s) payée(s) sur {periodLabel} · {Number(flPaid.total_ttc || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} TTC
                {flPaid.with_file < flPaid.count ? ` — ⚠ ${flPaid.count - flPaid.with_file} sans fichier Drive` : ""}
              </p>
            ) : (
              <p className="text-[11px] text-amber-700" data-testid="email-modal-freelance-missing">
                ⚠ Aucune facture freelance payée sur {periodLabel} — une confirmation sera demandée avant l&apos;envoi.
              </p>
            )}
          </div>
          {/* Iter-239 — supporting-documents ZIP */}
          <div className="border border-zinc-200 rounded-md p-3 space-y-2" data-testid="email-modal-zip-block">
            <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer">
              <input
                type="checkbox" checked={includeZip}
                onChange={(e) => setIncludeZip(e.target.checked)}
                data-testid="email-modal-include-zip" className="h-3 w-3"
              />
              Joindre le ZIP des pièces justificatives du mois
            </label>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              Factures clients émises · factures fournisseurs (cabinet) · notes de frais payées + reçus · justificatifs de paiement · déclarations de paiement · relevé bancaire + rapport de rapprochement + synthèse des écarts.
              Archivé sur Drive (Settings → Maintenance → General) sous <span className="font-mono">{year}_{String(month).padStart(2, "0")}</span>.
              Si le ZIP dépasse 22 Mo, un lien Drive est inséré dans le mail à la place.
            </p>
            {pkg?.status === "DONE" && !pkgProgress && (
              <div className="flex items-center gap-2 text-[11px] text-emerald-700" data-testid="email-modal-zip-ready">
                <span>
                  ZIP prêt ({((pkg.zip_size || 0) / (1024 * 1024)).toFixed(1)} Mo · {pkg.files_count || 0} fichier(s){pkg.skipped ? ` · ${pkg.skipped} ignoré(s)` : ""})
                  {pkg.generated_at ? ` — généré le ${String(pkg.generated_at).slice(0, 10)}` : ""}
                </span>
                {pkg.zip_drive_url && (
                  <a href={pkg.zip_drive_url} target="_blank" rel="noreferrer" className="underline text-sky-700" data-testid="email-modal-zip-drive-link">Drive</a>
                )}
                <button onClick={regenerateZip} disabled={sending} className="underline text-zinc-500 hover:text-zinc-700 disabled:opacity-50" data-testid="email-modal-zip-regenerate">
                  Régénérer
                </button>
              </div>
            )}
            {pkg?.status === "DONE" && pkg.drive_warning && (
              <p className="text-[11px] text-amber-600">{pkg.drive_warning}</p>
            )}
            {pkgProgress && (
              <div className="space-y-1" data-testid="email-modal-zip-progress">
                <div className="flex justify-between text-[11px] text-zinc-600">
                  <span>{pkgProgress.step}</span>
                  <span className="font-mono">{pkgProgress.progress}%</span>
                </div>
                <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-[width] duration-500" style={{ width: `${pkgProgress.progress}%` }} />
                </div>
              </div>
            )}
          </div>
          <ExtraAttachmentsPicker uploadUrl="/admin/monthly-statement/attachments" value={extraAtts}
            onChange={setExtraAtts} disabled={sending} testid="email-modal-extra-attachments" />
          <div className="text-[11px] text-zinc-500 italic">
            Un pixel de tracking est automatiquement inclus dans le corps HTML pour suivre l&apos;ouverture du mail (accusé de réception).
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Annuler</Button>
          <Button onClick={submit} disabled={sending} data-testid="email-modal-send">
            {sending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function HistoryModal({ open, onClose, items, periodLabel }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="monthly-statement-history-modal">
        <DialogHeader>
          <DialogTitle>Historique des envois — {periodLabel}</DialogTitle>
        </DialogHeader>
        {items.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">Aucun envoi pour cette période.</div>
        ) : (
          <div className="space-y-1.5">
            {items.map((it) => (
              <div key={it.token} className="border border-zinc-200 rounded-md px-3 py-2 text-xs" data-testid={`history-item-${it.token}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-zinc-700">{it.sent_at?.slice(0, 19).replace("T", " ")}</span>
                  {it.opened_at ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" title={`Ouvert ${it.open_count || 1} fois`}>
                      <Eye size={12} /> Ouvert ({it.open_count || 1}×)
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-zinc-400">
                      <EyeOff size={12} /> Non ouvert
                    </span>
                  )}
                </div>
                <div className="text-zinc-900 font-medium mt-1">{it.sent_subject}</div>
                <div className="text-zinc-500 mt-0.5">
                  À : {(it.sent_to || []).join(", ")}
                  {(it.sent_cc || []).length > 0 && <span> · Cc : {(it.sent_cc || []).join(", ")}</span>}
                </div>
                <div className="text-zinc-400 mt-0.5">Par {it.sent_by_name}</div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
